import { useCallback, useEffect } from "react";
import { useQueryClient } from "react-query";

import { apiUrls } from "config";
import { useCompute } from "api";
import { useGetDataUploadReducer } from "utils/file-upload-status";

import { useGetCleanupSpec, useGetInvalidCleanupCols } from "./create-specs";

export function useGetUploadData() {
  const queryClient = useQueryClient();
  const compute = useCompute<{}>(apiUrls.upload_data, false);
  const [uploadStatus, dispatch] = useGetDataUploadReducer();

  const createData = useCallback(
    async (files: File | File[]) => {
      if (Array.isArray(files) && files.length === 0) {
        const err = new Error("No files selected!!!");
        throw err;
      }
      if (!Array.isArray(files)) {
        files = [files];
      }
      dispatch({ type: "init", payload: { files } });
      (files as File[]).map(async (f) => {
        try {
          const resp = await compute({
            files: f,
            onUploadProgress: (progress) =>
              dispatch({
                type: "update-progress",
                payload: { file: f.name, progress: progress * 100 },
              }),
          });
          if (resp.status === "FAILURE") {
            dispatch({
              type: "update-status",
              payload: {
                file: f.name,
                completed: resp.result?.completed,
                error: resp.result?.error
                  ? resp.result.error_message ||
                    resp.result.message ||
                    "Unable to Process Data"
                  : undefined,
              },
            });
          } else if (resp.status === "SUCCESS") {
            dispatch({
              type: "update-status",
              payload: { file: f.name, completed: true },
            });
          }
          queryClient.invalidateQueries(apiUrls.data);
        } catch (e) {
          dispatch({
            type: "update-status",
            payload: {
              file: f.name,
              completed: true,
              error: e.message || e.detail?.file || "Unable to Process Data",
            },
          });
        }
      });
    },
    [compute, dispatch]
  );

  useEffect(() => {
    return () => {
      dispatch({ type: "reset" });
    };
  }, [dispatch]);

  return { createData, uploadStatus };
}

export function useGetCreate<T>(url: string) {
  const compute = useCompute<T>(url, true);

  const createData = useCallback(
    async (spec: T) => {
      return await compute({ data: spec });
    },
    [url, compute]
  );

  return createData;
}

export const useGetCreateCleanData = (fileId: string) => {
  const spec = useGetCleanupSpec(fileId);
  const invalidCleanupCols = useGetInvalidCleanupCols(fileId);

  const url = apiUrls.create_data_clean;
  const compute = useCompute(url, false);

  return useCallback(
    async (newName: string) => {
      if (!newName) {
        throw Error("Name is mandatory!");
      }
      if (invalidCleanupCols.length >= 1) {
        throw Error(
          `Invalid cleanup params for columns: ${invalidCleanupCols.join(", ")}`
        );
      }
      const finalSpec = { source_data: fileId, name: newName, ...spec };
      const resp = await compute({ data: finalSpec });
      if (!resp.result) {
        throw Error("Unable to create new data!");
      }
      const { completed, data, error_message, message } = resp.result;
      if (!completed) {
        throw Error(error_message || message || "Unable to create new data");
      }

      return data[0] as string;
    },
    [fileId, spec, invalidCleanupCols, compute]
  );
};
